import React, {useCallback, useMemo} from 'react';
import {Box, Stack, useTheme} from '@mui/material';
import {ActiveElement, ChartData, ChartEvent, ChartOptions, DefaultDataPoint, ScaleTypeRegistry, TooltipItem} from 'chart.js';
import {Line} from 'react-chartjs-2';
import {Align, Anchor, TextAlign} from "chartjs-plugin-datalabels/types/options";
import DashboardWidgetFooter from "../../dashboard-widget/footer";
import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from "../../../../../../../../hooks/use-search";
import {DashboardWidgetLayoutConfig, DashboardWidgetOrderByField} from "../../../types";
import {GetMinHeightReturnValue} from "../../../dashboard-widget.config";

// Interfaces
export interface DataPoint {
    [key: string]: string | number;  // Allow any key-value pairs
}

export interface YAxisConfig {
    id: string;
    position: 'left' | 'top' | 'right' | 'bottom' | 'center'
}

export interface LineConfig {
    dataKey: string;        // The key in the data object to plot
    label: string;          // Label for the legend
    color: string;          // Line color
    yAxisId: string;       // Which y-axis this line belongs to
    dataLabelConfig?: DataLabelConfig;  // Data label configuration
}

export interface TooltipConfig {
    key: string;           // The key in the data object to display
    label: string;         // Label to show in tooltip
    forLine?: string | string[];      // The line this tooltip belongs to
    format?: (value: string | number) => string;  // Optional formatter
}

export interface FiltersConfig {
    enabled: boolean;
    onClick?: (key: string, point: DataPoint) => void;
}

export interface DataLabelConfig {
    enabled?: boolean;
    format?: (value: string | number) => string;
    color?: (value: string | number) => string;
    align?: Align;
    anchor?: Anchor;
    textAlign?: TextAlign;
    offset?: number;
}

export interface LineChartConfig {
    xAxisKey?: string;                    // Key for x-axis values
    xAxisKeys?: string[];                  // Optional multiple keys for x-axis values
    sortByKey?: string;                  // Key to sort by
    sortByKeys?: string[]; // Optional multiple keys to sort by
    sortBy?: (a: DataPoint[keyof DataPoint], b: DataPoint[keyof DataPoint]) => number; // Sorting function
    multiSortBy?: (a: DataPoint, b: DataPoint) => number; // Multi-field sorting function
    xAxisFormatter?: (...value: (string | number)[]) => string;  // Optional formatter for x-axis values
    yAxes?: YAxisConfig[];               // Configuration for y-axes
    lines: LineConfig[];                // Lines to plot
    tooltipConfig: TooltipConfig[];     // Tooltip configuration
    filtersConfig?: FiltersConfig;      // Filters interaction configuration
    dataLabelConfig?: DataLabelConfig;  // Data label configuration
}

interface LineChartProps {
    data: DataPoint[];           // Array of data points
    config: LineChartConfig;     // Chart configuration - now required
    minHeight: GetMinHeightReturnValue,
    isLoading: boolean;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    underlyingDataVisibility?: DashboardWidgetLayoutConfig['underlyingDataVisibility'];
    orderByFields?: DashboardWidgetOrderByField[]; // Fields available for ordering
    hasPagination?: boolean;
}

const formatValue = (value: string | number, format?: (value: number) => string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    return format ? format(numValue) : numValue.toLocaleString();
};

/**
 * Line Chart Component
 *
 * Example usage:
 * ```tsx
 * const config: LineChartConfig = {
 *     xAxisKey: 'year',
 *     yAxes: [
 *         {
 *             id: 'value',
 *             position: 'left',
 *             prefix: '$',
 *             format: (value) => value.toLocaleString()
 *         },
 *         {
 *             id: 'quantity',
 *             position: 'right',
 *             format: (value) => value.toLocaleString()
 *         }
 *     ],
 *     lines: [
 *         {
 *             dataKey: 'Value_YTD',
 *             label: 'Value YTD',
 *             color: '#0088FE',
 *             yAxisId: 'value'
 *         },
 *         {
 *             dataKey: 'Qty_YTD',
 *             label: 'Quantity YTD',
 *             color: '#00C49F',
 *             yAxisId: 'quantity'
 *         }
 *     ],
 *     tooltipConfig: [
 *         {
 *             key: 'Value_YTD',
 *             label: 'Value',
 *             format: (value) => `$${value.toLocaleString()}`
 *         },
 *         {
 *             key: 'Qty_YTD',
 *             label: 'Quantity',
 *             format: (value) => value.toLocaleString()
 *         },
 *         {
 *             key: 'Av_Unit_Price',
 *             label: 'Average Unit Price',
 *             format: (value) => `$${value.toLocaleString()}`
 *         },
 *         {
 *             key: 'Av_Margin',
 *             label: 'Average Margin',
 *             format: (value) => `${value.toFixed(2)}%`
 *         }
 *     ]
 * };
 * ```
 */
export const DashboardLineChartWidget: React.FC<LineChartProps> = ({
                                                                       data,
                                                                       config,
                                                                       minHeight,
                                                                       isLoading,
                                                                       paginationInfo,
                                                                       setPaginationInfo,
                                                                       orderBy,
                                                                       setOrderBy,
                                                                       underlyingDataVisibility,
                                                                       orderByFields = [],
                                                                       hasPagination,
                                                                   }) => {
    const theme = useTheme();
    const {xAxisFormatter, filtersConfig: {onClick: filtersConfigOnClick} = {}, sortBy} = config;

    const sortedData = useMemo(() => {
        if (!!config.sortByKey && !!sortBy && (orderBy?.field === undefined || !orderBy?.field?.length || !orderByFields.length)) {
            return [...data].sort((a, b) => sortBy(a[config.sortByKey!], b[config.sortByKey!]));
        }
        if (!!config.sortByKeys && !!config.multiSortBy && (orderBy?.field === undefined || !orderBy?.field?.length || !orderByFields.length)) {
            return [...data].sort((a, b) => config.multiSortBy!(a, b));
        }
        return data;
    }, [config.multiSortBy, config.sortByKey, config.sortByKeys, data, orderBy?.field, orderByFields.length, sortBy]);


    /**
     * Handle click events on the chart
     */
    const handleClick = useCallback((event: ChartEvent, elements: ActiveElement[]) => {
        if (elements.length > 0 && config.filtersConfig?.enabled && filtersConfigOnClick) {
            const index = elements[0].index;
            const point = sortedData[index];
            filtersConfigOnClick(config.xAxisKey ?? config.xAxisKeys?.[0] ?? "", point);
        }
    }, [config.filtersConfig?.enabled, config.xAxisKey, config.xAxisKeys, filtersConfigOnClick, sortedData]);

    const chartData = useMemo<ChartData<'line', DefaultDataPoint<'line'>, string>>(() => {
        const xAxisKeys = config.xAxisKeys && config.xAxisKeys.length > 0 ? config.xAxisKeys : (config.xAxisKey ? [config.xAxisKey] : []);
        return ({
            labels: sortedData.map(item => xAxisFormatter?.(...xAxisKeys.map(key => item[key]?.toString())) ?? xAxisKeys.map(key => item[key]?.toString()).join(' ')),
            datasets: config
                .lines
                .filter(line => underlyingDataVisibility?.[line.dataKey] !== false) // Filter out hidden lines
                .map(line => ({
                    label: line.label,
                    data: sortedData.map(item => item[line.dataKey] as number),
                    borderColor: line.color,
                    backgroundColor: line.color,
                    yAxisID: line.yAxisId,
                    tension: 0.4,
                    datalabels: {
                        display: line.dataLabelConfig?.enabled ?? false,
                        anchor: line.dataLabelConfig?.anchor,
                        align: line.dataLabelConfig?.align,
                        textAlign: line.dataLabelConfig?.textAlign,
                        offset: line.dataLabelConfig?.offset,
                        formatter: (value, context) => {
                            const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                            return formatValue(dataPoint ?? "", line.dataLabelConfig?.format);
                        },
                        color: (context) => {
                            const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                            return line.dataLabelConfig?.color ? line.dataLabelConfig.color(dataPoint) : theme.palette.text.primary;
                        }
                    },
                }))
        })
    }, [config.xAxisKeys, config.xAxisKey, config.lines, sortedData, xAxisFormatter, underlyingDataVisibility, theme.palette.text.primary]);

    const options = useMemo<ChartOptions<'line'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleClick,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        borderColor: 'red',
        onHover: (event, chartElement) => {
            if (chartElement && event?.native?.target && config.filtersConfig?.enabled) {
                (event.native.target as any).style?.setProperty?.('cursor', 'pointer');
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                align: 'start' as const,
                labels: {
                    usePointStyle: true,
                },
                display: config.lines.filter(bar => underlyingDataVisibility?.[bar.dataKey] !== false).length > 1,
            },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'line'>[]) => {
                        return xAxisFormatter ? xAxisFormatter(items[0].label) : items[0].label;
                    },
                    label: (context: TooltipItem<'line'>) => {
                        // include the lineFor which the tooltip is shown
                        return config.tooltipConfig
                            // .filter(item => !(underlyingDataVisibility?.[item.key] !== false))
                            .filter(item => !item.forLine || (typeof item.forLine === 'string' ? item.forLine === context.dataset.label : item.forLine.includes(context.dataset.label ?? "")))
                            .map(config => {
                                const rawValue = sortedData[context.dataIndex][config.key] as number;
                                const formattedValue = formatValue(rawValue, config.format);
                                return `${config.label}: ${formattedValue}`;
                            });
                    }
                }
            },
            datalabels: {
                display: config.dataLabelConfig?.enabled ?? false,
                anchor: config.dataLabelConfig?.anchor,
                align: config.dataLabelConfig?.align,
                textAlign: config.dataLabelConfig?.textAlign,
                offset: config.dataLabelConfig?.offset,
                clamp: true,
                padding: -2,
                formatter: (value, context) => {
                    const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                    return formatValue(dataPoint ?? "", config.dataLabelConfig?.format);
                },
                color: (context) => {
                    const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                    return config.dataLabelConfig?.color ? config.dataLabelConfig.color(dataPoint) : theme.palette.text.primary;
                }
            },
        },
        scales: {
            x: {
                grid: {
                    color: theme.palette.mode === 'light' ? '#f3f3f3' : '#454545',
                },
                ticks: {
                    color: theme.palette.text.primary,
                },
            },
            ...(config.yAxes
                ?.filter(yAxis => underlyingDataVisibility?.[yAxis.id] !== false) // Filter out hidden y-axes
                ?.reduce((acc, yAxis) => ({
                    ...acc,
                    [yAxis.id]: {
                        type: 'linear' as const,
                        display: true,
                        position: yAxis.position,
                        beginAtZero: true,
                        grid: {
                            drawOnChartArea: yAxis.position === 'left',
                            color: theme.palette.mode === 'light' ? '#f3f3f3' : '#454545',
                        },
                        ticks: {
                            color: theme.palette.text.primary,
                            format: {compactDisplay: 'short', currencyDisplay: 'symbol', notation: 'compact'},
                        }
                    } as DeepPartial<ScaleTypeRegistry['linear']['options']>,
                }), {}) ?? {}),
        }
    }), [handleClick, config.lines, config.dataLabelConfig, config.yAxes, config.filtersConfig?.enabled, config.tooltipConfig, theme.palette.mode, theme.palette.text.primary, underlyingDataVisibility, xAxisFormatter, sortedData]);


    return (
        <Stack direction={"column"} gap={0.5}>
            <Box sx={{width: '100%', height: '100%', minHeight: minHeight}}>
                <Line data={chartData} options={options}/>
            </Box>
            <DashboardWidgetFooter
                paginationInfo={paginationInfo}
                setPaginationInfo={setPaginationInfo}
                isLoading={isLoading}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                orderByFields={orderByFields}
                hasPagination={hasPagination}
                data={data}
            />
        </Stack>
    );
};

export default DashboardLineChartWidget; 